/**
 * 🔒 The file type a file row's icon draws (decision 3011; the owner's icon set
 * of 2026-10-11, *"here are some icons to use"*): twenty kinds, one meaning
 * each, and ETC for any other. The kind is read from the served version's
 * media type first and the name's extension second; neither naming a kind
 * draws ETC.
 *
 * 🔨 A Word file and its older format share the DOC icon, a spreadsheet and its
 * older format the XLS icon, a slide deck either way the PPT icon: the set is
 * one icon per kind of work, and the band reads the kind's own label.
 */
export const FILE_KINDS = ['pdf', 'doc', 'xls', 'csv', 'txt', 'jpg', 'png', 'gif', 'svg', 'zip', 'dwg', 'dxf', 'dgn', 'ppt', 'json', 'xml', 'html', 'mp3', 'mp4', 'etc'] as const;
export type FileKind = (typeof FILE_KINDS)[number];

/** The words in the icon's black band, white bold capitals. */
export const FILE_KIND_LABEL: Readonly<Record<FileKind, string>> = {
  pdf: 'PDF', doc: 'DOC', xls: 'XLS', csv: 'CSV', txt: 'TXT', jpg: 'JPG', png: 'PNG', gif: 'GIF', svg: 'SVG', zip: 'ZIP',
  dwg: 'DWG', dxf: 'DXF', dgn: 'DGN', ppt: 'PPT', json: 'JSON', xml: 'XML', html: 'HTML', mp3: 'MP3', mp4: 'MP4', etc: 'ETC',
};

const BY_MIME: Readonly<Record<string, FileKind>> = {
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'doc',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xls',
  'text/csv': 'csv',
  'text/plain': 'txt',
  'image/jpeg': 'jpg',
  'image/pjpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/svg+xml': 'svg',
  'application/zip': 'zip',
  'application/x-zip-compressed': 'zip',
  'image/vnd.dwg': 'dwg',
  'image/x-dwg': 'dwg',
  'application/acad': 'dwg',
  'application/x-dwg': 'dwg',
  'image/vnd.dxf': 'dxf',
  'image/x-dxf': 'dxf',
  'application/dxf': 'dxf',
  'application/x-dxf': 'dxf',
  'application/x-dgn': 'dgn',
  'image/vnd.dgn': 'dgn',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'ppt',
  'application/json': 'json',
  'application/xml': 'xml',
  'text/xml': 'xml',
  'text/html': 'html',
  'audio/mpeg': 'mp3',
  'audio/mp3': 'mp3',
  'video/mp4': 'mp4',
};

const BY_EXTENSION: Readonly<Record<string, FileKind>> = {
  pdf: 'pdf', doc: 'doc', docx: 'doc', xls: 'xls', xlsx: 'xls', csv: 'csv', txt: 'txt', jpg: 'jpg', jpeg: 'jpg', png: 'png', gif: 'gif', svg: 'svg', zip: 'zip',
  dwg: 'dwg', dxf: 'dxf', dgn: 'dgn', ppt: 'ppt', pptx: 'ppt', json: 'json', xml: 'xml', html: 'html', htm: 'html', mp3: 'mp3', mp4: 'mp4',
};

/** 🔒 The kind a file row's icon draws: the media type first, then the name's extension, else ETC (decision 3011). */
export function fileKindOf(mimeType: string | null | undefined, filename: string | null | undefined): FileKind {
  const media = (mimeType ?? '').split(';')[0]!.trim().toLowerCase();
  const byMime = BY_MIME[media];
  if (byMime) return byMime;
  const name = (filename ?? '').trim().toLowerCase();
  const dot = name.lastIndexOf('.');
  const byExtension = dot > 0 ? BY_EXTENSION[name.slice(dot + 1)] : undefined;
  return byExtension ?? 'etc';
}
